/**
 * Base Popup Component
 * Reusable popup foundation with Framer Motion animations
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

export interface BasePopupProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  showCloseButton?: boolean;
  className?: string;
}

const BasePopup: React.FC<BasePopupProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  showCloseButton = true,
  className = '',
}) => {
  // Prevent body scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
          />

          {/* Popup Container */}
          <div className="fixed inset-0 flex items-center justify-center p-4 z-[9999] pointer-events-none overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 30 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`bg-gray-50 rounded-3xl shadow-2xl max-w-lg w-full pointer-events-auto relative my-8 max-h-[90vh] flex flex-col border border-blue-500/20 ${className}`}
            >
              {/* Close Button */}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="absolute top-5 right-5 text-white/80 hover:text-white transition-colors z-10 bg-blue-500/20 hover:bg-blue-500/30 rounded-full p-2 border border-blue-400/30"
                  aria-label="Close popup"
                >
                  <X size={20} />
                </button>
              )}

              {/* Header */}
              <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-8 py-10 rounded-t-3xl text-white flex-shrink-0 border-b-2 border-blue-500/30">
                <h2 className="text-3xl font-bold mb-3 leading-tight bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">{title}</h2>
                {subtitle && <p className="text-gray-300 text-base leading-relaxed">{subtitle}</p>}
              </div>

              {/* Content - Scrollable */}
              <div className="px-8 py-6 overflow-y-auto flex-1">
                {children}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BasePopup;
